import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, useAuth } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import { CITY, DIETS } from "../mock";
import { MapPin, ChevronDown, ChevronRight, Copy, AlertTriangle } from "lucide-react";
import { isDiagnosticsMode } from "../lib/utils";
import {
  fetchVendors, fetchVendorDetail, createMenuItem, updateMenuItem,
  patchItemAvailability, deleteMenuItem, listVendorOrders, updateOrderStatus,
  fetchPlans, patchVendorSettings, fetchSubscriptions, updateSubscription, sendReminder, fetchCustomers,
  resetDemoData
} from "../lib/api";

const CUSTOMIZATION_LABELS = {
  "no-onions": "No onions",
  "no-pickles": "No pickles",
  "no-peppers": "No peppers",
  "extra-protein": "Extra protein",
  "no-sauce": "No sauce",
  "sauce-side": "Sauce on the side",
};

export default function Admin() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, isPilot } = useAuth();

  const [vendors, setVendors] = useState([]);
  const [activeVendorId, setActiveVendorId] = useState("");
  const [activeVendor, setActiveVendor] = useState(null);
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [subs, setSubs] = useState([]);
  const [subsFilter, setSubsFilter] = useState("active");
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchVendors(CITY.label).then((vs) => {
      setVendors(vs);
      // Use the vendor's own company_id/vendor_id if set; otherwise fall back to first result
      const ownId = user?.company_id || user?.vendor_id;
      const defaultId = ownId || vs?.[0]?.id;
      if (defaultId) setActiveVendorId(defaultId);
    });
    fetchPlans().then(setPlans);
  }, []);

  useEffect(() => {
    if (!activeVendorId) return;
    fetchVendorDetail(activeVendorId).then((detail) => { setActiveVendor(detail.company); setMenu(detail.menu || []); });
    listVendorOrders(activeVendorId).then(setOrders);
    fetchSubscriptions(activeVendorId, subsFilter).then(setSubs);
    fetchCustomers(activeVendorId).then(setCustomers);
  }, [activeVendorId, subsFilter]);

  // Profile tab state
  const [profileName, setProfileName] = useState("");
  const [profileDesc, setProfileDesc] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileHours, setProfileHours] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);

  useEffect(() => {
    if (activeVendor) {
      setProfileName(activeVendor.name || "");
      setProfileDesc(activeVendor.description || "");
      setProfileImage(activeVendor.image || "");
      setProfileHours(activeVendor.hours || "");
    }
  }, [activeVendor]);

  async function saveProfile() {
    setProfileSaving(true);
    try {
      const updated = await patchVendorSettings(activeVendorId, {
        name: profileName,
        description: profileDesc,
        image: profileImage,
        hours: profileHours,
      });
      setActiveVendor(updated);
      toast({ title: "Profile saved" });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Could not save profile" });
    } finally {
      setProfileSaving(false);
    }
  }

  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  function onAddNew() { setEditing({ id: `new-${Date.now()}`, name: "", desc: "", price: 10, image: "", diet_tags: [], available: true, company_id: activeVendorId, calories: 0, macros: { carbs_g: 0, protein_g: 0, fat_g: 0 }, ingredients: "" }); setOpen(true); }
  function onEdit(item) { setEditing({ ...item }); setOpen(true); }

  async function saveItem() {
    try {
      if (editing.id?.startsWith("new-")) { const payload = { ...editing }; delete payload.id; const created = await createMenuItem(activeVendorId, payload); setMenu((m) => [...m, created]); }
      else { const updated = await updateMenuItem(activeVendorId, editing.id, editing); setMenu((m) => m.map((it) => (it.id === updated.id ? updated : it))); }
      toast({ title: "Saved", description: `${editing.name} updated` }); setOpen(false);
    } catch (e) { toast({ title: "Error", description: e?.response?.data?.detail || "Could not save item" }); }
  }

  async function deleteItem(item) {
    try { await deleteMenuItem(activeVendorId, item.id); setMenu((m) => m.filter((x) => x.id !== item.id)); toast({ title: "Removed", description: `${item.name} deleted` }); }
    catch (e) { toast({ title: "Minimum 4 items", description: e?.response?.data?.detail || "Cannot delete below 4" }); }
  }

  async function toggleAvailability(item) {
    try { const updated = await patchItemAvailability(activeVendorId, item.id, !(item.available !== false)); setMenu((m) => m.map((x) => (x.id === updated.id ? updated : x))); }
    catch (e) { toast({ title: "Minimum 4 available", description: e?.response?.data?.detail || "Cannot disable below 4" }); }
  }

  async function setOrderStatusLocal(id, status) { const updated = await updateOrderStatus(id, status); setOrders((os) => os.map((o) => (o.id === id ? updated : o))); }

  function toggleExpand(id) { setExpanded(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); }

  // Settings local state for plan prices, modifier fees, delivery fee/radius, days
  const [planPrices, setPlanPrices] = useState({});
  const [modifierFees, setModifierFees] = useState({});
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [delFee, setDelFee] = useState("");
  const [delRadius, setDelRadius] = useState("");
  const [deliveryDays, setDeliveryDays] = useState([]);
  const [pickupDays, setPickupDays] = useState([]);
  const [taxRateInput, setTaxRateInput] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(()=>{
    if(activeVendor){
      setPlanPrices(activeVendor.plan_prices || {});
      setModifierFees(activeVendor.modifier_fees || {});
      setDeliveryEnabled((activeVendor.delivery || []).includes('weekly'));
      setDelFee(activeVendor.delivery_fee ?? "");
      setDelRadius(activeVendor.delivery_radius_miles ?? "");
      setDeliveryDays(activeVendor.delivery_days || []);
      setPickupDays(activeVendor.pickup_days || []);
      setTaxRateInput(typeof activeVendor.tax_rate === 'number' ? (activeVendor.tax_rate * 100).toString() : "");
    }
  },[activeVendor]);

  async function saveSettings(patch){
    const updated = await patchVendorSettings(activeVendorId, patch);
    setActiveVendor(updated);
  }

  async function saveAllSettings() {
    setSettingsSaving(true);
    try {
      const delivery = deliveryEnabled ? ['weekly', 'pickup'] : ['pickup'];
      const updated = await patchVendorSettings(activeVendorId, {
        plan_prices: Object.fromEntries(Object.entries(planPrices).filter(([,v]) => v !== '')),
        modifier_fees: Object.fromEntries(Object.entries(modifierFees).filter(([,v]) => v !== '')),
        delivery,
        delivery_fee: delFee === '' ? null : parseFloat(delFee),
        delivery_radius_miles: delRadius === '' ? null : parseFloat(delRadius),
        delivery_days: deliveryDays,
        pickup_days: pickupDays,
        tax_rate: taxRateInput === '' ? null : parseFloat(taxRateInput) / 100,
      });
      setActiveVendor(updated);
      toast({ title: "Settings saved" });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Could not save settings" });
    } finally {
      setSettingsSaving(false);
    }
  }

  const isCompany = user && (user.role === "company" || user.role === "admin" || user.role === "vendor");
  if (!isCompany) {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-10">
          <Card><CardContent className="p-6">
            <h1 className="text-2xl font-semibold">Company Dashboard</h1>
            <p className="text-sm text-neutral-600 mt-2">This dashboard is for meal prep companies only.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => navigate("/")}>Back to customer app</Button>
            </div>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  const offeredSet = new Set(activeVendor?.plans_offered || []);
  const allMealCounts = [4,5,7,8,10,14];

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">Company Dashboard</h1>
          <div className="text-sm text-neutral-600">{activeVendor?.name}</div>
        </div>

        {/* Invite link card */}
        {activeVendor && (() => {
          const inviteToken = activeVendor.invite_token || activeVendor.id;
          const inviteUrl = `${window.location.origin}/join/${inviteToken}`;
          return (
            <Card className="mt-4 border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-blue-900 mb-1">Customer invite link</div>
                    <div className="text-xs text-blue-700 mb-2">Share this link to invite customers to your menu.</div>
                    <code className="block text-xs bg-white px-3 py-2 rounded border border-blue-200 break-all text-neutral-700">
                      {inviteUrl}
                    </code>
                  </div>
                  <Button
                    className="bg-blue-900 text-white hover:bg-blue-800 flex-shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(inviteUrl);
                      toast({ title: "Copied!", description: "Invite link copied to clipboard" });
                    }}
                    data-testid="copy-invite-link-btn"
                  >
                    <Copy size={14} className="mr-2" /> Copy link
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <Tabs defaultValue="orders" className="mt-4">
          <TabsList>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="menu">Menu</TabsTrigger>
            <TabsTrigger value="plans">Plans & Delivery</TabsTrigger>
            <TabsTrigger value="subs">Subscriptions / Customers</TabsTrigger>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            {isDiagnosticsMode() && <TabsTrigger value="pilot">Dev Tools</TabsTrigger>}
          </TabsList>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <Button variant="outline" onClick={() => listVendorOrders(activeVendorId).then(setOrders)}>Refresh</Button>
            </div>
            {orders.length === 0 ? (
              <Card><CardContent className="p-8 text-center">
                <div className="text-lg font-semibold text-neutral-800 mb-2">No orders yet</div>
                <div className="text-sm text-neutral-500 mb-4">Share your invite link with customers to get your first order.</div>
                {activeVendor && (() => {
                  const inviteToken = activeVendor.invite_token || activeVendor.id;
                  const inviteUrl = `${window.location.origin}/join/${inviteToken}`;
                  return (
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <code className="text-xs bg-neutral-50 px-3 py-2 rounded border break-all text-neutral-700">{inviteUrl}</code>
                      <Button
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(inviteUrl);
                          toast({ title: "Copied!", description: "Invite link copied to clipboard" });
                        }}
                      >
                        <Copy size={14} className="mr-2" /> Copy invite link
                      </Button>
                    </div>
                  );
                })()}
              </CardContent></Card>
            ) : (
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead></TableHead><TableHead className="min-w-[120px]">Order</TableHead><TableHead>Customer</TableHead><TableHead>Items</TableHead><TableHead>Plan</TableHead><TableHead>Fulfillment</TableHead><TableHead>Total</TableHead><TableHead className="w-[220px]">Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {orders.map(o => (
                    <React.Fragment key={o.id}>
                      <TableRow>
                        <TableCell className="w-[36px]"><button onClick={()=> setExpanded(prev=>{ const n=new Set(prev); n.has(o.id)? n.delete(o.id): n.add(o.id); return n; })} className="text-neutral-600 hover:text-black">{expanded.has(o.id)? <ChevronDown size={16}/> : <ChevronRight size={16}/>}</button></TableCell>
                        <TableCell className="text-sm">{o.id.slice(-8)}</TableCell>
                        <TableCell className="text-sm">{o.customer?.name}</TableCell>
                        <TableCell className="text-sm">{(o.items||[]).map(i=>i.name).join(', ')}</TableCell>
                        <TableCell className="text-sm">{o.plan?.label}</TableCell>
                        <TableCell className="text-sm">{o.fulfillment==='weekly'? 'Weekly delivery':'Pickup'}</TableCell>
                        <TableCell className="text-sm font-semibold">${(o.totals?.subtotal||0).toFixed(2)}</TableCell>
                        <TableCell>
                          <Select value={o.status} onValueChange={(v)=> setOrderStatusLocal(o.id, v)}><SelectTrigger className="bg-white"><SelectValue /></SelectTrigger><SelectContent>{[{id:'pending',label:'Pending'},{id:'preparing',label:'Preparing'},{id:'out_for_delivery',label:'Out for delivery'},{id:'completed',label:'Completed'},{id:'canceled',label:'Canceled'}].map(s=> <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent></Select>
                        </TableCell>
                      </TableRow>
                      {expanded.has(o.id) && (
                        <TableRow><TableCell colSpan={8}><div className="bg-[#faf7f1] border-t p-3 rounded-b-md grid md:grid-cols-3 gap-3">
                          <div><div className="text-xs font-semibold mb-1">Customer</div><div className="text-sm">{o.customer?.name} {o.customer?.id? <span className="text-neutral-500">• {o.customer.id}</span>: null}</div><div className="text-xs text-neutral-600">{o.customer?.email}</div><div className="mt-2 text-xs font-semibold">Address</div><div className="flex items-start gap-2 text-sm mt-1"><MapPin size={14} className="mt-0.5" /><span>{o.customer?.address || '—'}</span></div></div>
                          <div><div className="text-xs font-semibold mb-1">Items & modifiers</div><div className="space-y-1">{(o.items||[]).map((it, idx)=> (<div key={idx} className="text-sm"><span className="font-medium">{it.name}</span><span className="text-neutral-500"> • {it.meals} meals</span>{it.customizations?.length>0 && (<div className="text-xs text-neutral-600 mt-0.5">{it.customizations.map(cid => CUSTOMIZATION_LABELS[cid] || cid.replace(/-/g,' ')).join(', ')}</div>)}</div>))}</div></div>
                          <div><div className="text-xs font-semibold mb-1">Plan & fulfillment</div><div className="text-sm">{o.plan?.label}</div><div className="text-sm text-neutral-600">{o.fulfillment==='weekly'? 'Weekly delivery':'Pickup'}</div><div className="mt-2 text-xs font-semibold">Totals</div><div className="text-sm">Subtotal: ${(o.totals?.subtotal||0).toFixed(2)}</div></div>
                        </div></TableCell></TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
            )}
          </TabsContent>

          {/* MENU */}
          <TabsContent value="menu" className="mt-4">
            <div className="flex items-center justify-between mb-3"><h2 className="font-semibold">Menu items (minimum 4)</h2><Button onClick={onAddNew} className="bg-black text-white hover:bg-neutral-800">Add item</Button></div>
            <Card><CardContent className="p-0"><Table><TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Calories</TableHead><TableHead>Macros</TableHead><TableHead>Available</TableHead><TableHead className="w-[220px]">Actions</TableHead></TableRow></TableHeader><TableBody>{menu.map(item => (<TableRow key={item.id}><TableCell><div className="flex items-center gap-3"><img src={item.image} alt={item.name} loading="lazy" className="w-14 h-12 object-cover rounded border" /><div><div className="text-sm font-medium">{item.name}</div><div className="text-xs text-neutral-600 line-clamp-2 max-w-[420px]">{item.desc}</div><div className="text-xs text-neutral-600 mt-1">Ingredients: {item.ingredients || '—'}</div></div></div></TableCell><TableCell className="text-sm">{item.calories || '—'}</TableCell><TableCell className="text-xs text-neutral-600">{item.macros? `${item.macros.carbs_g||0}C / ${item.macros.protein_g||0}P / ${item.macros.fat_g||0}F g` : '—'}</TableCell><TableCell><Switch checked={item.available !== false} onCheckedChange={() => toggleAvailability(item)} /></TableCell><TableCell><div className="flex gap-2"><Button variant="outline" onClick={() => onEdit(item)}>Edit</Button><Button variant="destructive" onClick={() => deleteItem(item)}>Delete</Button></div></TableCell></TableRow>))}</TableBody></Table></CardContent></Card>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editing?.id?.startsWith("new-") ? "Add item" : "Edit item"}</DialogTitle>
                </DialogHeader>
                {editing && (
                  <div className="space-y-5 pt-1">
                    {/* Top two-column layout */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* Left: text fields */}
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Item name</label>
                          <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Grilled Chicken Bowl" className="bg-white" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Description</label>
                          <Textarea value={editing.desc} onChange={e => setEditing({ ...editing, desc: e.target.value })} placeholder="Short description for customers" className="bg-white" rows={3} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Ingredients</label>
                          <Input value={editing.ingredients||''} onChange={e => setEditing({ ...editing, ingredients: e.target.value })} placeholder="Chicken, rice, broccoli…" className="bg-white" />
                        </div>
                      </div>
                      {/* Right: photo + calories */}
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Photo URL</label>
                          <Input value={editing.image} onChange={e => setEditing({ ...editing, image: e.target.value })} placeholder="https://..." className="bg-white" />
                          {editing.image && (
                            <img src={editing.image} alt="Preview" className="mt-2 h-28 w-full object-cover rounded-lg border" />
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-neutral-600 block mb-1">Calories</label>
                          <Input type="number" step="1" value={editing.calories||0} onChange={e => setEditing({ ...editing, calories: parseInt(e.target.value||0) })} className="bg-white" />
                        </div>
                      </div>
                    </div>

                    <hr className="border-neutral-100" />

                    {/* Macros row */}
                    <div>
                      <div className="text-xs font-medium text-neutral-600 mb-2">Macros</div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { key: 'carbs_g', label: 'Carbs (g)' },
                          { key: 'protein_g', label: 'Protein (g)' },
                          { key: 'fat_g', label: 'Fat (g)' },
                        ].map(({ key, label }) => (
                          <div key={key} className="flex flex-col items-center gap-1">
                            <Input
                              type="number" step="1"
                              value={editing.macros?.[key] || 0}
                              onChange={e => setEditing({ ...editing, macros: { ...(editing.macros||{}), [key]: parseFloat(e.target.value||0) } })}
                              className="bg-white text-center"
                            />
                            <span className="text-xs text-neutral-500">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <hr className="border-neutral-100" />

                    {/* Diet tags */}
                    <div>
                      <div className="text-xs font-medium text-neutral-600 mb-2">Diet tags</div>
                      <div className="flex flex-wrap gap-2">
                        {DIETS.map(d => {
                          const checked = (editing.diet_tags || []).includes(d.id);
                          return (
                            <label key={d.id} className={`text-xs px-3 py-1 rounded-full border cursor-pointer transition-colors ${checked ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 hover:border-neutral-400'}`}>
                              <input type="checkbox" className="hidden" checked={checked} onChange={() => {
                                const set = new Set(editing.diet_tags || []);
                                checked ? set.delete(d.id) : set.add(d.id);
                                setEditing({ ...editing, diet_tags: Array.from(set) });
                              }} />
                              {d.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <hr className="border-neutral-100" />

                    {/* Availability + actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch checked={editing.available !== false} onCheckedChange={(v) => setEditing({ ...editing, available: v })} />
                        <span className="text-sm text-neutral-700">Available for ordering</span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={saveItem} className="bg-black text-white hover:bg-neutral-800">Save item</Button>
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* PLANS & DELIVERY */}
          <TabsContent value="plans" className="mt-4">
            <Card><CardContent className="p-6 space-y-6">

              {/* Offered meal counts */}
              <div>
                <div className="font-semibold mb-2">Offered meal plans</div>
                <div className="flex flex-wrap gap-2">
                  {allMealCounts.map(n => {
                    const checked = offeredSet.has(n);
                    return (
                      <label key={n} className={`text-xs px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${checked ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 hover:border-neutral-400'}`}>
                        <input type="checkbox" className="hidden" checked={checked} onChange={async () => {
                          const next = new Set(activeVendor?.plans_offered || []);
                          checked ? next.delete(n) : next.add(n);
                          const updated = await patchVendorSettings(activeVendorId, { plans_offered: Array.from(next) });
                          setActiveVendor(updated);
                        }} />
                        {n} meals / week
                      </label>
                    );
                  })}
                </div>
              </div>

              <hr className="border-neutral-100" />

              {/* Meal plan pricing */}
              <div>
                <div className="font-semibold mb-3">Meal plan pricing</div>
                <div className="grid grid-cols-3 gap-4">
                  {allMealCounts.filter(n => offeredSet.has(n)).map(n => {
                    const planId = `sub-${n}`;
                    return (
                      <div key={planId} className="flex flex-col gap-1">
                        <label className="text-xs text-neutral-500">{n} meals / week</label>
                        <div className="flex items-center border rounded-md bg-white overflow-hidden">
                          <span className="px-2 text-sm text-neutral-400">$</span>
                          <input
                            type="number" step="0.01"
                            value={planPrices?.[planId] ?? ''}
                            onChange={e => setPlanPrices(p => ({ ...p, [planId]: e.target.value }))}
                            placeholder="0.00"
                            className="flex-1 py-2 pr-2 text-sm outline-none bg-transparent"
                          />
                        </div>
                        <span className="text-xs text-neutral-400">per meal</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <hr className="border-neutral-100" />

              {/* Add-on fees */}
              <div>
                <div className="font-semibold mb-0.5">Add-on fees</div>
                <div className="text-xs text-neutral-500 mb-3">Charged per meal on top of base price</div>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(CUSTOMIZATION_LABELS).map(([id, label]) => (
                    <div key={id} className="flex flex-col gap-1">
                      <label className="text-xs text-neutral-500">{label}</label>
                      <div className="flex items-center border rounded-md bg-white overflow-hidden">
                        <span className="px-2 text-sm text-neutral-400">+$</span>
                        <input
                          type="number" step="0.01"
                          value={modifierFees?.[id] ?? ''}
                          onChange={e => setModifierFees(p => ({ ...p, [id]: e.target.value }))}
                          placeholder="0.00"
                          className="flex-1 py-2 pr-2 text-sm outline-none bg-transparent"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <hr className="border-neutral-100" />

              {/* Fulfillment */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="font-semibold">Fulfillment</span>
                  {isPilot && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Pickup only</span>
                  )}
                </div>
                <div className="space-y-3">
                  {!isPilot && (
                    <div className="flex items-center gap-3">
                      <Switch checked={deliveryEnabled} onCheckedChange={setDeliveryEnabled} />
                      <span className="text-sm text-neutral-700">Delivery enabled</span>
                    </div>
                  )}
                  {!isPilot && deliveryEnabled && (
                    <div className="grid md:grid-cols-2 gap-3 pl-1">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-neutral-500">Delivery days</label>
                        <Input value={deliveryDays.join(', ')} onChange={e => setDeliveryDays(e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="bg-white" placeholder="e.g. Monday, Thursday" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-neutral-500">Delivery fee ($)</label>
                        <Input type="number" step="0.01" value={delFee} onChange={e => setDelFee(e.target.value)} className="bg-white" placeholder="0.00" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-neutral-500">Delivery radius (miles)</label>
                        <Input type="number" step="0.1" value={delRadius} onChange={e => setDelRadius(e.target.value)} className="bg-white" placeholder="e.g. 10" />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs text-neutral-500">Pickup days</label>
                    <Input value={pickupDays.join(', ')} onChange={e => setPickupDays(e.target.value.split(',').map(s => s.trim()).filter(Boolean))} className="bg-white" placeholder="e.g. Sunday" />
                  </div>
                  <div className="flex flex-col gap-1 max-w-[200px]">
                    <label className="text-xs text-neutral-500">Tax rate (%)</label>
                    <Input type="number" step="0.01" value={taxRateInput} onChange={e => setTaxRateInput(e.target.value)} className="bg-white" placeholder="e.g. 8.25" />
                  </div>
                </div>
              </div>

              <hr className="border-neutral-100" />

              <div className="flex justify-end">
                <Button onClick={saveAllSettings} disabled={settingsSaving} className="bg-black text-white hover:bg-neutral-800">
                  {settingsSaving ? "Saving…" : "Save settings"}
                </Button>
              </div>

            </CardContent></Card>
          </TabsContent>

          {/* SUBSCRIPTIONS / CUSTOMERS */}
          <TabsContent value="subs" className="mt-4">
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="font-semibold">Subscriptions</div>
                <Select value={subsFilter} onValueChange={setSubsFilter}>
                  <SelectTrigger className="w-[150px] bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => fetchSubscriptions(activeVendorId, subsFilter).then(setSubs)}>Refresh</Button>
              </div>
              
              {subs.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-neutral-500">No {subsFilter === 'all' ? '' : subsFilter} subscriptions found.</div>
                    <div className="text-xs text-neutral-400 mt-1">Subscriptions will appear here once customers complete checkout.</div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Customer</TableHead>
                          <TableHead>Plan</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Week</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead className="w-[200px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subs.map(sub => (
                          <TableRow key={sub.id}>
                            <TableCell>
                              <div className="text-sm font-medium">{sub.user_name || sub.user_id?.slice(-8)}</div>
                              <div className="text-xs text-neutral-500">{sub.user_email || '—'}</div>
                            </TableCell>
                            <TableCell className="text-sm">{sub.plan_id?.replace('sub-', '')} meals</TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                sub.status === 'active' ? 'bg-green-100 text-green-700' :
                                sub.status === 'paused' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-neutral-100 text-neutral-600'
                              }`}>
                                {sub.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{sub.week_id || '—'}</TableCell>
                            <TableCell className="text-sm font-medium">${((sub.total_cents || 0) / 100).toFixed(2)}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {sub.status === 'active' && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={async () => {
                                      const updated = await updateSubscription(sub.id, { status: 'paused' });
                                      setSubs(s => s.map(x => x.id === sub.id ? updated : x));
                                    }}
                                  >
                                    Pause
                                  </Button>
                                )}
                                {sub.status === 'paused' && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={async () => {
                                      const updated = await updateSubscription(sub.id, { status: 'active' });
                                      setSubs(s => s.map(x => x.id === sub.id ? updated : x));
                                    }}
                                  >
                                    Resume
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={async () => {
                                    await sendReminder(sub.id);
                                    toast({ title: "Reminder sent", description: "Customer will receive a reminder email." });
                                  }}
                                >
                                  Remind
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Customers Section */}
            <div className="mt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="font-semibold">Customers</div>
                <Button variant="outline" onClick={() => fetchCustomers(activeVendorId).then(setCustomers)}>Refresh</Button>
              </div>
              
              {customers.length === 0 ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="text-neutral-500">No customers yet.</div>
                    <div className="text-xs text-neutral-400 mt-1">Customers who have ordered from this vendor will appear here.</div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Orders</TableHead>
                          <TableHead>Joined</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customers.map(cust => (
                          <TableRow key={cust.id}>
                            <TableCell className="text-sm font-medium">{cust.name || 'Unknown'}</TableCell>
                            <TableCell className="text-sm text-neutral-600">{cust.email || '—'}</TableCell>
                            <TableCell className="text-sm">{cust.order_count || 0}</TableCell>
                            <TableCell className="text-sm text-neutral-500">
                              {cust.created_at ? new Date(cust.created_at).toLocaleDateString() : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* PROFILE */}
          <TabsContent value="profile" className="mt-4">
            <Card><CardContent className="p-6 space-y-4 max-w-xl">
              <div>
                <label className="text-sm font-medium block mb-1">Business name</label>
                <Input value={profileName} onChange={e => setProfileName(e.target.value)} className="bg-white" placeholder="Your business name" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Description</label>
                <Textarea value={profileDesc} onChange={e => setProfileDesc(e.target.value)} className="bg-white" placeholder="Tell customers about your service" rows={3} />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Photo URL</label>
                <Input value={profileImage} onChange={e => setProfileImage(e.target.value)} className="bg-white" placeholder="https://..." />
                {profileImage && (
                  <img src={profileImage} alt="Preview" className="mt-2 h-24 w-full object-cover rounded-lg border" />
                )}
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Hours</label>
                <Input value={profileHours} onChange={e => setProfileHours(e.target.value)} className="bg-white" placeholder="e.g. Mon–Fri 9am–6pm" />
              </div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-2">
                <p className="font-semibold">Vendor Certification</p>
                <p className="text-xs leading-relaxed">By saving your profile and listing products on PrepMate, you confirm that: (1) you hold all required food handler certifications and food business licenses required by applicable Texas law; (2) all food products you sell are prepared, packaged, and labeled in compliance with applicable food safety regulations; and (3) all ingredient, allergen, and nutritional information you provide is accurate and complete. You understand that you are solely responsible for your food products and agree to the <a href="/vendor-agreement" className="underline" target="_blank" rel="noopener noreferrer">Vendor Agreement</a>.</p>
              </div>
              <Button
                onClick={saveProfile}
                disabled={profileSaving}
                className="bg-black text-white hover:bg-neutral-800"
              >
                {profileSaving ? "Saving..." : "Save profile"}
              </Button>
            </CardContent></Card>
          </TabsContent>

          {/* PILOT TOOLS - Diagnostics only */}
          {isDiagnosticsMode() && (
            <TabsContent value="pilot" className="mt-4">
              <PilotToolsSection toast={toast} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// Pilot Tools Component (diagnostics-only)
function PilotToolsSection({ toast }) {
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  async function handleResetDemoData() {
    if (resetConfirmText !== "RESET") {
      toast({ title: "Confirmation required", description: "Type RESET to confirm." });
      return;
    }
    
    setIsResetting(true);
    try {
      const result = await resetDemoData();
      toast({ 
        title: "Demo data reset", 
        description: `Cleared ${result.deleted_count} subscription(s) for demo user.` 
      });
      setResetConfirmText("");
    } catch (err) {
      toast({ 
        title: "Reset failed", 
        description: err?.response?.data?.detail || "Could not reset demo data." 
      });
    } finally {
      setIsResetting(false);
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-amber-600">
          <AlertTriangle size={18} />
          <span className="font-semibold">Dev Tools (Diagnostics Mode)</span>
        </div>
        <div className="text-sm text-neutral-600">
          These tools are only visible in diagnostics mode (?debug=1 or localStorage prepmate_debug="1").
        </div>
        
        <div className="border-t pt-4">
          <div className="font-medium mb-2">Reset Demo User Data</div>
          <div className="text-sm text-neutral-600 mb-3">
            This will clear all subscriptions for the demo user (demo@prepmate.com) and unbind any vendor association.
            The demo account will return to a clean state.
          </div>
          <div className="flex items-center gap-3">
            <Input 
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="Type RESET to confirm"
              className="w-48 bg-white"
            />
            <Button 
              variant="destructive"
              onClick={handleResetDemoData}
              disabled={isResetting || resetConfirmText !== "RESET"}
            >
              {isResetting ? "Resetting..." : "Reset Demo Data"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}